import { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '../contexts/ToastContext';
import i18n from '../i18n';
import { getApiErrorMessage } from '../utils/apiErrorMessages';

// Generic hook for API calls
export const useApi = (apiFunction, dependencies = [], options = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const toast = useToast();

  const {
    showSuccessToast = false,
    showErrorToast = true,
    successMessage = i18n.t('common.operationSuccessful'),
    immediate = true,
  } = options;

  const execute = useCallback(async (...args) => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if apiFunction is null or undefined
      if (!apiFunction) {
        setData(null);
        setLoading(false);
        return null;
      }
      
      const response = await apiFunction(...args);
      
      // Check if response exists before accessing its properties
      if (response && response.data) {
        setData(response.data);
        
        if (showSuccessToast) {
          toast.success(successMessage);
        }
        
        return response.data;
      } else {
        setData(null);
        return null;
      }
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'common.errorOccurred');
      
      setError(errorMessage);

      if (showErrorToast) {
        toast.error(errorMessage);
      }
      
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiFunction, showSuccessToast, showErrorToast, successMessage, toast]);

  useEffect(() => {
    if (immediate) {
      // Prevent unhandled promise rejections when an immediate request fails (e.g. 403/500).
      execute().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callers control reruns through the dependencies argument
  }, dependencies);

  return {
    data,
    loading,
    error,
    execute,
    refetch: execute,
  };
};

// Hook for paginated data
export const usePaginatedApi = (apiFunction, initialParams = {}) => {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });
  /** True on first paint when we will fetch, so list pages show a loader instead of an empty table flash. */
  const [loading, setLoading] = useState(() => Boolean(apiFunction));
  const [error, setError] = useState(null);
  const [params, setParams] = useState(initialParams);
  const paramsRef = useRef(initialParams);
  /** Incremented on each fetch start; responses from older generations are ignored (avoids stale list when params change quickly). */
  const fetchGenerationRef = useRef(0);
  /** After first successful fetch, avoid setLoading(true) so debounced refetches do not swap the table for a skeleton (flash). */
  const initialFetchDoneRef = useRef(false);
  const toast = useToast();

  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  useEffect(() => {
    initialFetchDoneRef.current = false;
    if (apiFunction) {
      setLoading(true);
    } else {
      setLoading(false);
      setData([]);
    }
  }, [apiFunction]);

  const fetchData = useCallback(async (newParams = {}) => {
    const generation = ++fetchGenerationRef.current;
    try {
      if (!initialFetchDoneRef.current) {
        setLoading(true);
      }
      setError(null);
      
      // Check if apiFunction is null or undefined
      if (!apiFunction) {
        setData([]);
        setLoading(false);
        return;
      }
      
      const mergedParams = { ...paramsRef.current, ...newParams };
      const response = await apiFunction(mergedParams);

      if (generation !== fetchGenerationRef.current) return;

      setData(response.data[Object.keys(response.data)[0]] || []);
      setPagination(response.data.pagination || {});
      setParams(mergedParams);
      paramsRef.current = mergedParams;
      initialFetchDoneRef.current = true;
    } catch (err) {
      if (generation !== fetchGenerationRef.current) return;

      const errorMessage = getApiErrorMessage(err, 'common.failedToFetchData');
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      if (generation === fetchGenerationRef.current) {
        setLoading(false);
      }
    }
  }, [apiFunction, toast]);

  const updateParams = useCallback((newParams) => {
    fetchData({ ...newParams, page: 1 });
  }, [fetchData]);

  const goToPage = useCallback((page) => {
    fetchData({ page });
  }, [fetchData]);

  const refresh = useCallback(() => {
    fetchData({});
  }, [fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    pagination,
    loading,
    error,
    params,
    updateParams,
    goToPage,
    refresh,
    refetch: refresh,
  };
};

// Hook for form submission
export const useFormSubmit = (submitFunction, options = {}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();

  const {
    onSuccess,
    onError,
    showSuccessToast = true,
    showErrorToast = true,
    successMessage = i18n.t('common.operationSuccessful'),
  } = options;

  const submit = useCallback(async (data) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await submitFunction(data);
      
      if (response && response.data) {
        if (showSuccessToast) {
          toast.success(successMessage);
        }
        
        if (onSuccess) {
          onSuccess(response.data);
        }
        
        return response.data;
      } else {
        return null;
      }
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'common.operationFailed');
      
      setError(errorMessage);
      
      if (showErrorToast) {
        toast.error(errorMessage);
      }
      
      if (onError) {
        onError(err);
      }

      throw err;
    } finally {
      setLoading(false);
    }
  }, [submitFunction, onSuccess, onError, showSuccessToast, showErrorToast, successMessage, toast]);

  return {
    submit,
    loading,
    error,
    clearError: () => setError(null),
  };
};

// Hook for async operations with loading state
export const useAsyncOperation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();

  const execute = useCallback(async (operation, options = {}) => {
    const {
      showSuccessToast = false,
      showErrorToast = true,
      successMessage = i18n.t('common.operationSuccessful'),
    } = options;

    try {
      setLoading(true);
      setError(null);
      
      const result = await operation();
      
      if (showSuccessToast) {
        toast.success(successMessage);
      }
      
      return result;
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'common.operationFailed');
      
      setError(errorMessage);
      
      if (showErrorToast) {
        toast.error(errorMessage);
      }

      throw err;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  return {
    execute,
    loading,
    error,
    clearError: () => setError(null),
  };
};

export default useApi;